import React from "react";
import clsx from "clsx";
import { format, parseISO } from "date-fns";
import { CircularProgress, makeStyles } from "@material-ui/core";
import { AccessTime, ErrorOutline, InsertDriveFile, Mic, Audiotrack } from "@material-ui/icons";
import LockIcon from "@material-ui/icons/Lock";
import MarkdownWrapper from "../MarkdownWrapper";
import { i18n } from "../../translate/i18n";

// Bolha "enviando": mesma cara da bolha enviada, com relógio no lugar do tick e a prévia
// local do anexo. Se o envio falhar, vira bolha de erro com "Tentar de novo" e "Descartar".

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const soft = isDark ? "rgba(233,237,239,0.6)" : "#667781";
  const danger = isDark ? "#ff8a80" : "#d93025";
  return {
    media: {
      position: "relative",
      display: "block",
      width: 280,
      maxWidth: "100%",
      margin: "0 0 3px -3px",
      borderRadius: 6,
      overflow: "hidden",
      backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(11,20,26,0.05)",
      "& img, & video": {
        display: "block",
        width: "100%",
        maxHeight: 320,
        objectFit: "cover",
      },
    },
    veil: {
      position: "absolute",
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "rgba(11,20,26,0.28)",
    },
    spinnerDisc: {
      width: 48,
      height: 48,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "rgba(11,20,26,0.55)",
      color: "#ffffff",
    },
    card: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      minWidth: 220,
      maxWidth: 340,
      margin: "0 0 4px -3px",
      padding: "9px 10px",
      borderRadius: 6,
      backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(11,20,26,0.05)",
    },
    cardIcon: {
      flex: "none",
      width: 38,
      height: 38,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(11,20,26,0.08)",
      color: soft,
      "& svg": { fontSize: 21 },
    },
    cardText: { flex: 1, minWidth: 0, whiteSpace: "normal" },
    cardName: {
      fontSize: 13.5,
      lineHeight: "18px",
      fontWeight: 500,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    cardSub: { fontSize: 11.5, lineHeight: "15px", color: soft },
    cardSpinner: { flex: "none", color: soft },
    errorIcon: { fontSize: 15, marginLeft: 2, color: danger },
    errorRow: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: "2px 10px",
      margin: "0 -2px 0 -4px",
      padding: "5px 6px 7px",
      borderTop: `1px solid ${isDark ? "rgba(233,237,239,0.14)" : "rgba(11,20,26,0.1)"}`,
      fontSize: 12.5,
      lineHeight: "18px",
      whiteSpace: "normal",
    },
    errorLabel: {
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      color: danger,
      fontWeight: 500,
      marginRight: "auto",
      "& svg": { fontSize: 16 },
    },
    action: {
      padding: "2px 4px",
      border: 0,
      borderRadius: 4,
      background: "transparent",
      font: "inherit",
      fontWeight: 600,
      color: isDark ? "#53bdeb" : "#027eb5",
      cursor: "pointer",
      "&:hover": { textDecoration: "underline" },
      "&:focus-visible": { outline: `2px solid ${isDark ? "#53bdeb" : "#027eb5"}`, outlineOffset: 1 },
      "&:disabled": { opacity: 0.5, cursor: "default", textDecoration: "none" },
    },
    actionMuted: { color: soft },
  };
});

export const formatFileSize = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} kB`;
  return `${(n / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
};

// Rodapé de erro, também usado na bolha definitiva quando o servidor avisa que não enviou.
export const SendErrorRow = ({ errorText, onRetry, onDiscard, busy, doneText }) => {
  const classes = useStyles();
  return (
    <div className={classes.errorRow} role="alert" onDoubleClick={(e) => e.stopPropagation()}>
      <span className={classes.errorLabel} title={errorText || undefined}>
        <ErrorOutline />
        {doneText || i18n.t("conversation.sending.failed")}
      </span>
      {onRetry && (
        <button type="button" className={classes.action} onClick={onRetry} disabled={busy}>
          {i18n.t("conversation.sending.retry")}
        </button>
      )}
      {onDiscard && (
        <button type="button" className={clsx(classes.action, classes.actionMuted)} onClick={onDiscard} disabled={busy}>
          {i18n.t("conversation.sending.discard")}
        </button>
      )}
    </div>
  );
};

const Spinner = ({ entry, size = 30 }) => {
  const determinate = entry.status === "sending" && entry.progress > 0 && entry.progress < 100;
  return (
    <CircularProgress
      size={size}
      thickness={4}
      color="inherit"
      variant={determinate ? "determinate" : "indeterminate"}
      value={determinate ? entry.progress : undefined}
    />
  );
};

const PendingMedia = ({ entry }) => {
  const classes = useStyles();
  const failed = entry.status === "error";

  if ((entry.mediaKind === "image" || entry.mediaKind === "video") && entry.previewUrl) {
    return (
      <span className={classes.media}>
        {entry.mediaKind === "image" ? (
          <img src={entry.previewUrl} alt={entry.fileName} />
        ) : (
          <video src={entry.previewUrl} muted playsInline preload="metadata" />
        )}
        {!failed && (
          <span className={classes.veil}>
            <span className={classes.spinnerDisc}>
              <Spinner entry={entry} />
            </span>
          </span>
        )}
      </span>
    );
  }

  const isAudio = entry.mediaKind === "audio";
  const sub = [
    isAudio && entry.voice ? null : formatFileSize(entry.fileSize),
    failed ? null : entry.status === "sending" && entry.progress > 0 && entry.progress < 100 ? `${entry.progress}%` : i18n.t("conversation.sending.sending"),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className={classes.card}>
      <span className={classes.cardIcon}>
        {isAudio ? entry.voice ? <Mic /> : <Audiotrack /> : <InsertDriveFile />}
      </span>
      <span className={classes.cardText}>
        <div className={classes.cardName}>
          {isAudio && entry.voice ? i18n.t("conversation.sending.voice") : entry.fileName}
        </div>
        <div className={classes.cardSub}>{sub}</div>
      </span>
      {!failed && (
        <span className={classes.cardSpinner}>
          <Spinner entry={entry} size={22} />
        </span>
      )}
    </div>
  );
};

const PendingBubble = ({ entry, className, bubbleClasses, quoted, renderText }) => {
  const classes = useStyles();
  const failed = entry.status === "error";
  // áudio de voz não tem legenda: o corpo é só um rótulo técnico
  const text = entry.kind === "media" && entry.mediaKind === "audio" && entry.voice ? "" : entry.body;

  return (
    <div
      className={className}
      id={`pending-${entry.localId}`}
      data-pending={entry.status}
      aria-busy={!failed}
    >
      {entry.kind === "media" && <PendingMedia entry={entry} />}
      {/* com o rodapé de erro, a hora ancora no bloco de texto (senão cairia em cima dos botões) */}
      <div className={bubbleClasses.textContentItem} style={failed ? { position: "relative" } : undefined}>
        {quoted}
        {text ? (renderText ? renderText(text) : <MarkdownWrapper>{text}</MarkdownWrapper>) : null}
        <span className={bubbleClasses.timestamp}>
          {entry.isPrivate && <LockIcon className={bubbleClasses.privateIcon} />}
          {format(parseISO(entry.createdAt), "HH:mm")}
          {failed ? (
            <ErrorOutline className={classes.errorIcon} />
          ) : (
            <AccessTime fontSize="small" className={bubbleClasses.ackIcons} titleAccess={i18n.t("conversation.sending.sending")} />
          )}
        </span>
      </div>
      {failed && (
        <SendErrorRow errorText={entry.errorText} onRetry={entry.retry} onDiscard={entry.onDiscard} />
      )}
    </div>
  );
};

export default PendingBubble;
